import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/system';
import * as Sentry from '@sentry/react';
import Http from 'services/Http';
import getSubdomain from 'utils/getSubdomain';
import { I18n } from 'utils/i18n';

const MSAL_ERROR_CODES = {
  AADSTS650052: 'SharePoint Online not available',
  AADSTS650057: 'Resource not authorized',
  AADSTS700016: 'App not found in tenant',
};

const parseAadstsCode = haystack => {
  const match = String(haystack || '').match(/AADSTS\d+/);
  return match ? match[0] : null;
};

const sharePointHostFromUrl = url => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const logPickerBreadcrumb = (message, data = {}) => {
  Sentry.addBreadcrumb({
    category: 'microsoft_picker',
    message,
    level: 'info',
    data,
  });
};

const logPickerFailure = (message, { step, sharePointUrl, tokenId, errorMessage }) => {
  const aadstsCode = parseAadstsCode(errorMessage);
  const extra = {
    msal_step: step,
    sharePointHost: sharePointUrl ? sharePointHostFromUrl(sharePointUrl) : null,
    companySubdomain: getSubdomain() || null,
    hasTokenId: Boolean(tokenId),
    aadsts_code: aadstsCode,
    errorMessage,
  };

  Sentry.addBreadcrumb({
    category: 'microsoft_picker',
    message,
    level: 'error',
    data: extra,
  });

  Sentry.captureMessage(message, {
    level: 'error',
    tags: {
      feature: 'microsoft_onedrive_picker',
      auth_path: 'server_picker_token',
      msal_step: step,
      ...(aadstsCode ? { aadsts_code: aadstsCode } : {}),
    },
    extra,
  });
};

const getMsalFriendlyError = error => {
  const haystack = error?.errorMessage || error?.message || '';
  const match = haystack.match(/AADSTS\d+/);
  if (match && MSAL_ERROR_CODES[match[0]]) {
    return I18n.t(`ChatFlowSettings.${MSAL_ERROR_CODES[match[0]]}`);
  }
  return error?.message || I18n.t('ChatFlowSettings.Unable to obtain authentication token');
};

const generateChannelId = () => `picker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Drives Microsoft's v8 OneDrive File Picker inside an iframe.
 *
 * @param {object}   params
 * @param {'files'|'folders'} params.mode   What the picker selects.
 * @param {() => Promise<string|null>} params.getSharePointUrl  Resolves the SharePoint base URL.
 * @param {string|(() => string)} params.tokenId  OAuth token document id for server-side token exchange.
 * @param {(items: Array) => void}     params.onPick           Called with picked item(s).
 * @returns {{ open: () => void, isOpen: boolean, error: string|null, PickerPortal: React.FC }}
 */
export default function useOneDriveFilePicker({
  mode = 'files',
  getSharePointUrl,
  tokenId,
  onPick,
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError] = useState(null);
  const pickerIframeRef = useRef(null);
  const channelIdRef = useRef(null);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const locale = useSelector(state => state.i18n.locale);

  // Keep latest callback/inputs without re-subscribing the message listener.
  const cbRef = useRef({ getSharePointUrl, onPick, tokenId, mode });
  cbRef.current = { getSharePointUrl, onPick, tokenId, mode };

  const getPickerToken = useCallback(async command => {
    const resolvedTokenId = (() => {
      const tid = cbRef.current.tokenId;
      return typeof tid === 'function' ? tid() : tid;
    })();

    let sharePointUrl = command?.resource;
    if (!sharePointUrl || sharePointUrl === 'https://onedrive.live.com/picker') {
      sharePointUrl = await cbRef.current.getSharePointUrl();
    }

    if (!resolvedTokenId) {
      logPickerFailure('microsoft_picker_missing_token_id', {
        step: 'picker_token_request',
        sharePointUrl,
        tokenId: resolvedTokenId,
        errorMessage: 'missing tokenId',
      });
      throw new Error(I18n.t('ChatFlowSettings.Unable to obtain authentication token'));
    }

    if (!sharePointUrl) {
      logPickerFailure('microsoft_picker_missing_sharepoint_url', {
        step: 'picker_token_request',
        sharePointUrl,
        tokenId: resolvedTokenId,
        errorMessage: 'missing sharePointUrl',
      });
      throw new Error(I18n.t('ChatFlowSettings.Personal account not supported'));
    }

    try {
      const { data } = await Http.post('/v1/microsoft-oauth/picker-token', {
        tokenId: resolvedTokenId,
        sharePointUrl,
      });
      if (!data?.accessToken) {
        throw new Error(I18n.t('ChatFlowSettings.Unable to obtain authentication token'));
      }
      logPickerBreadcrumb('microsoft_picker_token_ok', {
        sharePointHost: sharePointHostFromUrl(sharePointUrl),
        hasTokenId: true,
      });
      return data.accessToken;
    } catch (e) {
      const message = e?.data?.error?.message || e?.message || '';
      logPickerFailure('microsoft_picker_token_failed', {
        step: 'picker_token_request',
        sharePointUrl,
        tokenId: resolvedTokenId,
        errorMessage: message,
      });
      throw new Error(getMsalFriendlyError({ message, errorMessage: message }), { cause: e });
    }
  }, []);

  // Message handshake with the picker iframe.
  useEffect(() => {
    if (!showPicker) return undefined;
    let port = null;

    const channelMessageListener = async messageEvent => {
      const payload = messageEvent.data;
      if (payload.type !== 'command') {
        return;
      }
      port.postMessage({ type: 'acknowledge', id: messageEvent.data.id });
      const command = payload.data;

      switch (command.command) {
        case 'authenticate':
          try {
            const tkn = await getPickerToken(command);
            if (!tkn) throw new Error('Unable to obtain a token.');
            port.postMessage({
              type: 'result',
              id: messageEvent.data.id,
              data: { result: 'token', token: tkn },
            });
          } catch (e) {
            setError(e.message);
            setShowPicker(false);
            port.postMessage({
              type: 'result',
              id: messageEvent.data.id,
              data: { result: 'error', error: { code: 'unableToObtainToken', message: e.message } },
            });
          }
          break;
        case 'close':
          setShowPicker(false);
          break;
        case 'pick':
          try {
            const items = command.items || [];
            if (items.length > 0) cbRef.current.onPick(items);
            port.postMessage({
              type: 'result',
              id: messageEvent.data.id,
              data: { result: 'success' },
            });
            logPickerBreadcrumb('microsoft_picker_pick_ok', { itemCount: items.length });
            setShowPicker(false);
          } catch (e) {
            logPickerFailure('microsoft_picker_pick_failed', {
              step: 'iframe_pick',
              sharePointUrl: null,
              tokenId: null,
              errorMessage: e.message,
            });
            port.postMessage({
              type: 'result',
              id: messageEvent.data.id,
              data: { result: 'error', error: { code: 'unusableItem', message: e.message } },
            });
          }
          break;
        default:
          port.postMessage({
            type: 'result',
            id: messageEvent.data.id,
            data: {
              result: 'error',
              error: { code: 'unsupportedCommand', message: command.command },
            },
          });
          break;
      }
    };

    const initializeMessageListener = event => {
      if (event.source && event.source === pickerIframeRef.current?.contentWindow) {
        const message = event.data;
        if (message.type === 'initialize' && message.channelId === channelIdRef.current) {
          [port] = event.ports;
          port.addEventListener('message', channelMessageListener);
          port.start();
          port.postMessage({ type: 'activate' });
        }
      }
    };

    window.addEventListener('message', initializeMessageListener);
    return () => {
      window.removeEventListener('message', initializeMessageListener);
      if (port) port.removeEventListener('message', channelMessageListener);
    };
  }, [showPicker, getPickerToken]);

  const open = useCallback(() => {
    setError(null);
    setShowPicker(true);
    channelIdRef.current = generateChannelId();
    logPickerBreadcrumb('microsoft_picker_opened', { mode: cbRef.current.mode });

    const initPicker = async () => {
      if (!pickerIframeRef.current) {
        setTimeout(initPicker, 100);
        return;
      }
      const iframe = pickerIframeRef.current;
      iframe.src = 'about:blank';
      iframe.onload = async () => {
        try {
          const win = iframe.contentWindow;
          try {
            if (!win || !win.document) return;
          } catch {
            return; // navigated away from about:blank already
          }

          const baseUrl = await cbRef.current.getSharePointUrl();
          if (!baseUrl) {
            const msg = I18n.t('ChatFlowSettings.Personal account not supported');
            logPickerFailure('microsoft_picker_init_failed', {
              step: 'picker_init',
              sharePointUrl: null,
              tokenId: null,
              errorMessage: msg,
            });
            setError(msg);
            setShowPicker(false);
            return;
          }

          const accessToken = await getPickerToken({ resource: baseUrl });
          if (!accessToken) {
            const msg = I18n.t('ChatFlowSettings.Unable to obtain authentication token');
            setError(msg);
            setShowPicker(false);
            return;
          }

          const isFolders = cbRef.current.mode === 'folders';
          const options = {
            sdk: '8.0',
            entry: { oneDrive: { files: {} } },
            search: { enabled: true },
            authentication: {},
            messaging: { origin: window.location.origin, channelId: channelIdRef.current },
            typesAndSources: {
              mode: isFolders ? 'folders' : 'files',
              ...(isFolders ? {} : { filters: ['.xlsx', '.xls'] }),
            },
            theme: prefersDarkMode ? 'dark' : 'default',
            selection: { mode: 'single', maxCount: 1 },
          };

          const queryString = new URLSearchParams({
            filePicker: JSON.stringify(options),
            locale,
          });
          const url = `${baseUrl}/_layouts/15/FilePicker.aspx?${queryString}`;

          const form = win.document.createElement('form');
          form.setAttribute('action', url);
          form.setAttribute('method', 'POST');
          const tokenInput = win.document.createElement('input');
          tokenInput.setAttribute('type', 'hidden');
          tokenInput.setAttribute('name', 'access_token');
          tokenInput.setAttribute('value', accessToken);
          form.appendChild(tokenInput);
          win.document.body.appendChild(form);
          form.submit();
        } catch (e) {
          logPickerFailure('microsoft_picker_init_failed', {
            step: 'picker_init',
            sharePointUrl: null,
            tokenId: null,
            errorMessage: e.message,
          });
          setError(e.message);
          setShowPicker(false);
        }
      };
    };
    setTimeout(initPicker, 100);
  }, [getPickerToken, locale, prefersDarkMode]);

  const PickerPortal = useCallback(
    () =>
      showPicker ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <iframe
            ref={pickerIframeRef}
            title="OneDrive File Picker"
            style={{
              width: '90%',
              maxWidth: 1080,
              height: '90%',
              maxHeight: 680,
              border: 'none',
              borderRadius: 8,
              backgroundColor: 'white',
            }}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      ) : null,
    [showPicker],
  );

  return { open, isOpen: showPicker, error, PickerPortal };
}
